import { SlidersHorizontal, X, Clock } from "lucide-react";
import { useFilters } from "@/context/FiltersContext";

// Shows the Filters trigger PLUS chips of what's currently applied (each removable),
// so you always see what the view is filtered by. Works on both dark (mobile) and
// light (desktop) surfaces via the `variant` prop. Used on Calendar / List / Map / Results.
export default function FilterBar({ className = "", variant = "dark" }) {
  const {
    setFiltersOpen, activeFilterCount, assoc, toggleAssoc,
    country, setCountry, province, setProvince, status, setStatus,
    minMoney, setMinMoney, events, toggleEvent, circuits, toggleCircuit,
    closingSoon, toggleClosingSoon,
  } = useFilters();

  const light = variant === "light";
  const btnCls = light
    ? "bg-white border-[#D7DBE0] text-slate-700 hover:border-slate-400"
    : "bg-white/5 border-white/10 text-slate-200 active:bg-white/10";
  const chipActive = light
    ? "bg-[#C8000F]/10 border-[#C8000F]/30 text-[#C8000F]"
    : "bg-[#C8000F]/15 border-[#C8000F]/40 text-[#C8000F]";
  const chipMuted = light ? "bg-slate-100 border-slate-200 text-slate-400" : "bg-white/5 border-white/10 text-slate-400";
  const allCls = light ? "text-slate-400" : "text-slate-500";

  const chips = [];
  if (assoc.size > 0) {
    [...assoc].forEach((a) => chips.push({ k: `a-${a}`, label: a, clear: () => toggleAssoc(a) }));
  }
  if (country) chips.push({ k: "country", label: country, clear: () => setCountry("") });
  if (province) chips.push({ k: "prov", label: province, clear: () => setProvince("") });
  if (status) chips.push({ k: "status", label: `Entries: ${status}`, clear: () => setStatus("") });
  if (minMoney > 0) chips.push({ k: "money", label: `$${Math.round(minMoney / 1000)}k+`, clear: () => setMinMoney(0) });
  [...circuits].forEach((c) => chips.push({ k: `c-${c}`, label: c, clear: () => toggleCircuit(c) }));
  [...events].forEach((e) => chips.push({ k: `e-${e}`, label: e, clear: () => toggleEvent(e) }));

  return (
    <div className={`flex items-center gap-1.5 overflow-x-auto no-scrollbar ${className}`} data-testid="filter-bar">
      <button onClick={() => setFiltersOpen(true)} data-testid="view-filters-btn"
        className={`relative shrink-0 inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-2 rounded-lg border ${btnCls}`}>
        <SlidersHorizontal className="w-4 h-4" /> Filters
        {activeFilterCount > 0 && (
          <span data-testid="view-filters-count" className="min-w-[16px] h-4 px-1 rounded-full bg-[#C8000F] text-white text-[10px] font-bold flex items-center justify-center">{activeFilterCount}</span>
        )}
      </button>
      <button onClick={toggleClosingSoon} data-testid="closing-soon-chip"
        className={`shrink-0 inline-flex items-center gap-1.5 text-[12px] font-bold px-3 py-2 rounded-lg border whitespace-nowrap transition-colors ${closingSoon ? "bg-[#C8000F] border-[#C8000F] text-white" : btnCls}`}>
        <Clock className="w-4 h-4" /> Closes 7d
      </button>
      {chips.length === 0 ? (
        <span className={`text-[11px] px-1 shrink-0 whitespace-nowrap ${allCls}`}>Showing all</span>
      ) : chips.map((c) => (
        <button key={c.k} onClick={c.clear || undefined} disabled={!c.clear} data-testid={`filter-chip-${c.k}`}
          className={`shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-full whitespace-nowrap border ${c.muted ? chipMuted : chipActive}`}>
          {c.label} {c.clear && <X className="w-3 h-3" />}
        </button>
      ))}
    </div>
  );
}
